import React, { useState, useEffect, useCallback, ChangeEvent, MouseEvent } from 'react';
import { reportsAPI, userModerationAPI, handleApiError } from '../services/api';
import { ProfileImage } from '../utils/imageUtils';
import type { ReportStatus } from '../types';
import './ReportedUsers.css';

interface ReportedUserInfo {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_image?: string;
  is_banned?: boolean;
}

interface Reporter {
  first_name: string;
  last_name: string;
  email: string;
}

interface Report {
  id: string;
  reported_user: ReportedUserInfo;
  reporter: Reporter;
  reason: string;
  details?: string;
  status: ReportStatus;
  admin_notes?: string;
  created_at: string;
}

type FilterStatus = 'pending' | 'resolved' | 'dismissed' | 'all';

const ReportedUsers: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [reportsLoading, setReportsLoading] = useState<boolean>(true);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [reportsFilter, setReportsFilter] = useState<FilterStatus>('pending');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  const loadReports = useCallback(async (): Promise<void> => {
    setReportsLoading(true);
    setReportsError(null);
    try {
      const statusFilter = reportsFilter === 'all' ? null : reportsFilter;
      const response = await reportsAPI.getReportedUsers(statusFilter);
      const reportsData = response.data as unknown as { reports?: Report[] } | Report[];
      setReports(Array.isArray(reportsData) ? reportsData : (reportsData.reports || []));
    } catch (err) {
      setReportsError(handleApiError(err));
    } finally {
      setReportsLoading(false);
    }
  }, [reportsFilter]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleResolveReport = async (reportId: string, adminNotes: string = ''): Promise<void> => {
    try {
      await reportsAPI.resolveReport(reportId, adminNotes);
      loadReports();
      setShowModal(false);
      setSelectedReport(null);
    } catch (err) {
      alert('Грешка при разрешаването: ' + handleApiError(err));
    }
  };

  const handleDismissReport = async (reportId: string, adminNotes: string = ''): Promise<void> => {
    try {
      await reportsAPI.dismissReport(reportId, adminNotes);
      loadReports();
      setShowModal(false);
      setSelectedReport(null);
    } catch (err) {
      alert('Грешка при отхвърлянето: ' + handleApiError(err));
    }
  };

  // User Moderation Handlers
  const handleBanUser = async (userId: string, userName: string): Promise<void> => {
    const reason = prompt(`Причина за баниране на ${userName}:`);
    if (reason !== null && reason.trim()) {
      try {
        await userModerationAPI.banUser(userId, reason.trim());
        alert('Потребителят е успешно банирван.');
        loadReports();
      } catch (err) {
        alert('Грешка при банирането: ' + handleApiError(err));
      }
    }
  };

  const handleSuspendUser = async (userId: string, userName: string): Promise<void> => {
    const reason = prompt(`Причина за временно спиране на ${userName}:`);
    if (reason !== null && reason.trim()) {
      const durationInput = prompt('Продължителност в часове (по подразбиране 72):', '72');
      const duration = parseInt(durationInput || '72') || 72;

      try {
        await userModerationAPI.suspendUser(userId, reason.trim(), duration);
        alert(`Потребителят е временно спрян за ${duration} часа.`);
        loadReports();
      } catch (err) {
        alert('Грешка при спирането: ' + handleApiError(err));
      }
    }
  };

  const handleWarnUser = async (userId: string, userName: string): Promise<void> => {
    const reason = prompt(`Причина за предупреждение на ${userName}:`);
    if (reason !== null && reason.trim()) {
      try {
        await userModerationAPI.warnUser(userId, reason.trim());
        alert('Предупреждението е изпратено.');
        loadReports();
      } catch (err) {
        alert('Грешка при изпращането на предупреждение: ' + handleApiError(err));
      }
    }
  };

  const handleUnbanUser = async (userId: string, userName: string): Promise<void> => {
    const reason = prompt(`Причина за премахване на бан от ${userName}:`);
    if (reason !== null && reason.trim()) {
      try {
        await userModerationAPI.unbanUser(userId, reason.trim());
        alert('Банът е премахнат.');
        loadReports();
      } catch (err) {
        alert('Грешка при премахването на бан: ' + handleApiError(err));
      }
    }
  };

  const getStatusBadgeClass = (status: ReportStatus): string => {
    switch (status) {
      case 'pending': return 'badge-warning';
      case 'resolved': return 'badge-success';
      case 'dismissed': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  const getStatusText = (status: ReportStatus): string => {
    switch (status) {
      case 'pending': return 'Чака преглед';
      case 'resolved': return 'Разрешено';
      case 'dismissed': return 'Отхвърлено';
      default: return status;
    }
  };

  const getReasonText = (reason: string): string => {
    switch (reason) {
      case 'spam': return 'Спам';
      case 'inappropriate': return 'Неподходящо поведение';
      case 'safety': return 'Проблем с безопасността';
      default: return reason;
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('bg-BG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openReportModal = (report: Report): void => {
    setSelectedReport(report);
    setShowModal(true);
  };

  const closeModal = (): void => {
    setShowModal(false);
    setSelectedReport(null);
  };

  return (
    <div className="reports-page">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Докладвани потребители</h2>
          <p className="last-updated">Преглед и модерация на доклади за потребители</p>
          <div className="header-controls">
            <select
              value={reportsFilter}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setReportsFilter(e.target.value as FilterStatus)}
              className="filter-select"
            >
              <option value="pending">Чакащи преглед</option>
              <option value="resolved">Разрешени</option>
              <option value="dismissed">Отхвърлени</option>
              <option value="all">Всички доклади</option>
            </select>
          </div>
        </div>
        <button
          className={`floating-refresh ${reportsLoading ? 'loading' : ''}`}
          onClick={loadReports}
          disabled={reportsLoading}
          title="Обновяване на докладите"
        >
          <i className={`fas fa-sync-alt ${reportsLoading ? 'fa-spin' : ''}`}></i>
        </button>
      </div>

      {reportsError && (
        <div className="error-message" style={{ margin: '20px', padding: '15px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c66' }}>
          <i className="fas fa-exclamation-triangle"></i>
          {reportsError}
        </div>
      )}

      {reportsLoading ? (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-exclamation-triangle"></i>
            {reportsFilter === 'pending' ? 'Чакащи доклади' : 'Всички доклади'}
          </h3>
          <div style={{ padding: '40px', textAlign: 'center', background: 'var(--glass-background)', borderRadius: '8px', border: '1px solid var(--glass-border)', backdropFilter: 'var(--glass-backdrop)' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: 'var(--color-text-secondary)' }}></i>
            <p style={{ color: 'var(--color-text-secondary)', marginTop: '16px' }}>Зареждане на доклади...</p>
          </div>
        </div>
      ) : reports.length === 0 ? (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-exclamation-triangle"></i>
            {reportsFilter === 'pending' ? 'Чакащи доклади' : 'Всички доклади'}
          </h3>
          <div style={{ padding: '40px', textAlign: 'center', background: 'var(--glass-background)', borderRadius: '8px', border: '1px solid var(--glass-border)', backdropFilter: 'var(--glass-backdrop)' }}>
            <i className="fas fa-check-circle" style={{ fontSize: '48px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}></i>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '16px', margin: '0' }}>
              {reportsFilter === 'pending' ? 'Няма чакащи доклади' : 'Няма доклади'}
            </p>
          </div>
        </div>
      ) : (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-exclamation-triangle"></i>
            {reportsFilter === 'pending' ? 'Чакащи доклади' : 'Всички доклади'} ({reports.length})
          </h3>
          <div className="reports-list">
            {reports.map((report) => (
              <div key={report.id} className="report-card">
                <div className="report-header">
                  <div className="report-meta">
                    <div className="reported-user-info">
                      <ProfileImage
                        src={report.reported_user.profile_image}
                        alt="Profile"
                        className="user-avatar"
                      />
                      <div className="user-details">
                        <h3>{report.reported_user.first_name} {report.reported_user.last_name}</h3>
                        <span className="user-email">{report.reported_user.email}</span>
                      </div>
                    </div>
                    <div className="report-status">
                      <span className={`badge ${getStatusBadgeClass(report.status)}`}>
                        {getStatusText(report.status)}
                      </span>
                      <span className="report-date">{formatDate(report.created_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="report-content">
                  <div className="report-reason">
                    <strong>Причина:</strong> {getReasonText(report.reason)}
                  </div>
                  {report.details && (
                    <div className="report-details">
                      <strong>Детайли:</strong> {report.details}
                    </div>
                  )}
                  <div className="reporter-info">
                    <strong>Докладвано от:</strong> {report.reporter.first_name} {report.reporter.last_name}
                  </div>
                </div>

                {report.admin_notes && (
                  <div className="admin-notes">
                    <strong>Административни бележки:</strong> {report.admin_notes}
                  </div>
                )}

                <div className="report-actions">
                  <button
                    className="btn btn-info"
                    onClick={() => openReportModal(report)}
                  >
                    <i className="fas fa-eye"></i> Преглед
                  </button>
                  {report.status === 'pending' && (
                    <>
                      <button
                        className="btn btn-success"
                        onClick={() => {
                          const notes = prompt('Административни бележки (незадължително):');
                          if (notes !== null) {
                            handleResolveReport(report.id, notes);
                          }
                        }}
                        style={{ backgroundColor: '#22c55e', color: 'white' }}
                      >
                        <i className="fas fa-check"></i> Разреши
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          const notes = prompt('Причина за отхвърляне:');
                          if (notes !== null) {
                            handleDismissReport(report.id, notes);
                          }
                        }}
                        style={{ backgroundColor: '#ef4444', color: 'white' }}
                      >
                        <i className="fas fa-times"></i> Отхвърли
                      </button>
                    </>
                  )}
                </div>

                {/* User Moderation Actions */}
                <div className="moderation-actions" style={{ marginTop: '10px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px' }}>
                  <small style={{ color: 'var(--color-text-secondary)', display: 'block', marginBottom: '8px' }}>
                    Действия към потребител:
                  </small>
                  <div className="moderation-buttons">
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => handleWarnUser(report.reported_user.id, `${report.reported_user.first_name} ${report.reported_user.last_name}`)}
                      style={{ backgroundColor: '#f59e0b', color: 'white', marginRight: '5px' }}
                      title="Изпрати предупреждение"
                    >
                      <i className="fas fa-exclamation-triangle"></i> Предупреди
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleSuspendUser(report.reported_user.id, `${report.reported_user.first_name} ${report.reported_user.last_name}`)}
                      style={{ backgroundColor: '#f97316', color: 'white', marginRight: '5px' }}
                      title="Временно спиране"
                    >
                      <i className="fas fa-clock"></i> Спри
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleBanUser(report.reported_user.id, `${report.reported_user.first_name} ${report.reported_user.last_name}`)}
                      style={{ backgroundColor: '#dc2626', color: 'white', marginRight: '5px' }}
                      title="Перманентно баниране"
                    >
                      <i className="fas fa-ban"></i> Банирай
                    </button>
                    {report.reported_user.is_banned && (
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleUnbanUser(report.reported_user.id, `${report.reported_user.first_name} ${report.reported_user.last_name}`)}
                        style={{ backgroundColor: '#16a34a', color: 'white' }}
                        title="Премахни бан"
                      >
                        <i className="fas fa-check"></i> Премахни бан
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report Details Modal */}
      {showModal && selectedReport && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e: MouseEvent) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Детайли за доклад</h3>
              <button className="modal-close" onClick={closeModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="report-detail-section">
                <h4>Докладван потребител</h4>
                <div className="user-info-detailed">
                  <ProfileImage
                    src={selectedReport.reported_user.profile_image}
                    alt="Profile"
                    className="user-avatar-large"
                  />
                  <div>
                    <p><strong>Име:</strong> {selectedReport.reported_user.first_name} {selectedReport.reported_user.last_name}</p>
                    <p><strong>Email:</strong> {selectedReport.reported_user.email}</p>
                  </div>
                </div>
              </div>

              <div className="report-detail-section">
                <h4>Информация за доклада</h4>
                <p><strong>Причина:</strong> {getReasonText(selectedReport.reason)}</p>
                {selectedReport.details && (
                  <p><strong>Детайли:</strong> {selectedReport.details}</p>
                )}
                <p><strong>Дата:</strong> {formatDate(selectedReport.created_at)}</p>
                <p><strong>Статус:</strong> <span className={`badge ${getStatusBadgeClass(selectedReport.status)}`}>{getStatusText(selectedReport.status)}</span></p>
              </div>

              <div className="report-detail-section">
                <h4>Докладил потребител</h4>
                <p><strong>Име:</strong> {selectedReport.reporter.first_name} {selectedReport.reporter.last_name}</p>
                <p><strong>Email:</strong> {selectedReport.reporter.email}</p>
              </div>

              {selectedReport.admin_notes && (
                <div className="report-detail-section">
                  <h4>Административни бележки</h4>
                  <p>{selectedReport.admin_notes}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {selectedReport.status === 'pending' && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      const notes = prompt('Административни бележки (незадължително):');
                      if (notes !== null) {
                        handleResolveReport(selectedReport.id, notes);
                      }
                    }}
                    style={{ backgroundColor: '#22c55e', color: 'white' }}
                  >
                    <i className="fas fa-check"></i> Разреши
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      const notes = prompt('Причина за отхвърляне:');
                      if (notes !== null) {
                        handleDismissReport(selectedReport.id, notes);
                      }
                    }}
                    style={{ backgroundColor: '#ef4444', color: 'white' }}
                  >
                    <i className="fas fa-times"></i> Отхвърли
                  </button>
                </>
              )}
              <button className="btn btn-default" onClick={closeModal}>
                Затвори
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportedUsers;
